import { Component } from '@theme/component';

const MAX_COMPARE = 4;

/**
 * Wraps a collection grid: manages the "Compare" checkboxes on each card,
 * shows a floating bar once 2+ products are selected, and builds a simple
 * comparison table (real product data read from data-* attributes already
 * rendered server-side — no extra network requests).
 *
 * @extends {Component}
 */
export class ProductComparatorComponent extends Component {
  requiredRefs = ['floatingBar', 'floatingBarCount', 'drawer', 'tableBody', 'tableHead'];

  /** @type {Map<string, Record<string, string>>} */
  selected = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.updateUI();
  }

  /**
   * @param {Event} event
   */
  toggleCompare(event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    const card = checkbox.closest('[data-compare-id]');
    if (!(card instanceof HTMLElement)) return;

    const id = card.dataset.compareId ?? '';

    if (checkbox.checked) {
      if (this.selected.size >= MAX_COMPARE) {
        checkbox.checked = false;
        return;
      }

      this.selected.set(id, {
        title: card.dataset.compareTitle ?? '',
        price: card.dataset.comparePrice ?? '',
        image: card.dataset.compareImage ?? '',
        vendor: card.dataset.compareVendor ?? '',
        options: card.dataset.compareOptions ?? '',
        availability: card.dataset.compareAvailability ?? '',
      });
    } else {
      this.selected.delete(id);
    }

    this.updateUI();
  }

  updateUI() {
    const { floatingBar, floatingBarCount } = this.refs;
    const count = this.selected.size;

    floatingBar.hidden = count < 2;
    floatingBarCount.textContent = String(count);
  }

  openComparison() {
    this.buildTable();
    this.refs.drawer.open?.();
  }

  buildTable() {
    const { tableHead, tableBody } = this.refs;
    const entries = [...this.selected.values()];

    tableHead.innerHTML = `<tr><th scope="col"></th>${entries
      .map(
        (entry) => `
          <th scope="col">
            ${entry.image ? `<img src="${entry.image}" alt="" width="64" height="64" loading="lazy">` : ''}
            <span>${entry.title}</span>
          </th>`
      )
      .join('')}</tr>`;

    const rows = [
      { label: 'Price', key: 'price' },
      { label: 'Vendor', key: 'vendor' },
      { label: 'Available options', key: 'options' },
      { label: 'Availability', key: 'availability' },
    ];

    tableBody.innerHTML = rows
      .map(
        (row) => `
          <tr>
            <th scope="row">${row.label}</th>
            ${entries.map((entry) => `<td>${entry[row.key] || '—'}</td>`).join('')}
          </tr>`
      )
      .join('');
  }

  /**
   * @param {Event} event
   */
  filterByPrice(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const range = button.dataset.priceFilter;
    if (!range) return;

    for (const pill of this.querySelectorAll('.collection-showcase__filter-pill')) {
      pill.classList.toggle('is-active', pill === button);
    }

    const [minStr, maxStr] = range.split('-');
    const min = Number(minStr);
    const max = Number(maxStr);

    for (const card of this.querySelectorAll('[data-price-cents]')) {
      if (!(card instanceof HTMLElement)) continue;

      const price = Number(card.dataset.priceCents ?? 0);
      card.hidden = range !== 'all' && (price < min || price > max);
    }
  }

  clearSelection() {
    for (const checkbox of this.querySelectorAll('[data-compare-checkbox]')) {
      if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
    }

    this.selected.clear();
    this.updateUI();
  }
}

if (!customElements.get('product-comparator-component')) {
  customElements.define('product-comparator-component', ProductComparatorComponent);
}
